import type { OccluderRule } from "daedalus-core";
import type { PaletteName } from "../ui/tokens.ts";

/**
 * The box that stands for a character's body, in world pixels, centred on the feet and standing on the feet
 * line, and the rule by which a prop sorting in front of it fades (ADR-040): the occluder fade tests it, the
 * concealment of another player measures it, and the editor's stand-in is drawn, picked and faded by it (ADR-071
 * decisions 1 and 10), so the game and the editor read one constant. Daedalus counts the pixels; these numbers are
 * the game's. It is one box for every look a player wears (ADR-094 decision 34): the body standing, measured on the
 * look sheets' skin, 14 pixels wide and 29 high from the feet line, without hair, hat or tools, so no chosen piece
 * changes what fades or who is hidden.
 */

/**
 * The body box's width in world pixels, centred on the feet.
 */
export const BODY_WIDTH = 14;

/**
 * The body box's height in world pixels, standing on the feet line.
 */
export const BODY_HEIGHT = 29;

/**
 * How far a prop's art must rise above its base, in pixels, before it may fade at all: about the body's height plus
 * three tenths, so a low object never fades. Derived from the body box: its height times 44 over 34, the ratio the
 * author's choice of 44 stood in to the body of 34 it was set against, rounded (29 gives 37.5, so 38).
 */
export const OCCLUDER_MIN_HEIGHT = Math.round((BODY_HEIGHT * 44) / 34);

/**
 * The least share of the body box a prop's solid pixels must hide for it to fade. Tunable. The author's
 * choice (R-0011): 0.9, so only what hides nearly the whole character fades (buildings, leafy trees, large
 * rocks and structures) and a sign post, a fence or a bare tree never does.
 */
export const OCCLUDER_MIN_COVERAGE = 0.9;

/**
 * The alpha a prop that hides the character draws at.
 */
export const OCCLUDER_ALPHA = 0.45;

/**
 * The rule the game fades props over its local character by, registered as the stand-in's so the editor
 * fades by it too.
 */
export const OCCLUDERS: OccluderRule = { minHeight: OCCLUDER_MIN_HEIGHT, minCoverage: OCCLUDER_MIN_COVERAGE, alpha: OCCLUDER_ALPHA };

/**
 * The share of a remote entity's body box one prop drawn above it must hide for the viewer's world UI over it (its
 * plate, bar, bubble and numbers) to hide (ADR-061's amendment for P1.9-T73): the author's "about two-thirds".
 * Tunable.
 */
export const CONCEAL_COVERAGE = 2 / 3;

/**
 * The share below which a concealed entity shows again: under `CONCEAL_COVERAGE` by a small band, about one pixel of
 * sideways walking for a 14 pixel wide body, so an entity on the edge does not flicker. Tunable.
 */
export const REVEAL_COVERAGE = 0.6;

/**
 * The hysteresis a remote entity's concealment follows: hidden at `hideAt` or more, shown again below `showBelow`.
 */
export interface ConcealRule {
  readonly hideAt: number;
  readonly showBelow: number;
}

/**
 * The rule the game conceals remote entities' world UI by.
 */
export const CONCEALMENT: ConcealRule = { hideAt: CONCEAL_COVERAGE, showBelow: REVEAL_COVERAGE };

/**
 * The sight cone's full angle in degrees (ADR-061's amendment for P1.9-T75): the author's "about 90 degrees". Tunable.
 */
export const SIGHT_CONE_ANGLE = 90;

/**
 * How far the sight cone reaches from the local character's feet, in tiles: the author's "about 4 tiles". Tunable.
 */
export const SIGHT_CONE_REACH = 4;

/**
 * The alpha a tall prop in the sight cone eases to: the author's "about half". Above `OCCLUDER_ALPHA`, so a prop that
 * hides the character keeps the full fade. Tunable.
 */
export const SIGHT_CONE_ALPHA = 0.5;

/**
 * The time constant, in ms, with which the cone's axis follows the character's heading along the shorter arc, so a
 * turn by keys sweeps instead of jumping. Tunable.
 */
export const SIGHT_CONE_TURN_MS = 120;

/**
 * The time, in ms, a prop takes to fade the whole way from 1 to `SIGHT_CONE_ALPHA` as it enters the cone, and back as
 * it leaves, linearly, so a prop on the cone's edge as it sweeps does not pop. Tunable.
 */
export const SIGHT_CONE_FADE_MS = 200;

/**
 * How far to each side of the local character's feet the cover that gates the sight cone is measured, in tiles
 * (ADR-061's amendment for P1.9-T82): a square a little wider than the cone reaches, so the measure is of the place
 * and not of the one tree ahead. Tunable.
 */
export const SIGHT_DENSE_RADIUS = 5;

/**
 * The cover at which an open place turns dense and the sight cone turns on: the summed art of the props the cone could
 * dim, cut to the square, over the square's area. 1 is a forest; Aethervale's village reads 0.6 at most and a lone
 * tree 0.2. Tunable.
 */
export const SIGHT_DENSE_ON = 1;

/**
 * The cover below which a dense place turns open again and the sight cone turns off: under `SIGHT_DENSE_ON` by a band
 * wide enough that walking along a forest's edge does not toggle it. Tunable.
 */
export const SIGHT_DENSE_OFF = 0.7;

/**
 * The time, in ms, the sight cone's strength takes to ease the whole way from 0 to 1 as a place turns dense, and back
 * as it turns open, linearly. Tunable.
 */
export const SIGHT_DENSE_EASE_MS = 400;

/**
 * The longest the cover stands unmeasured while the character has not moved a tile, in ms: props painted in with a
 * chunk or placed by a live edit count within it. Tunable.
 */
export const SIGHT_DENSE_REFRESH_MS = 250;

/**
 * The knobs that gate the sight cone by how dense the place around the local character is: the square's reach in
 * tiles, the cover that turns the cone on and the one below which it turns off, the strength's ease and the longest
 * a measurement stands.
 */
export interface SightDenseRule {
  readonly radius: number;
  readonly onAt: number;
  readonly offBelow: number;
  readonly easeMs: number;
  readonly refreshMs: number;
}

/**
 * The sight cone's knobs together: the full angle in degrees, the reach in tiles, the alpha and the two eases, the
 * occluder fade's height floor, below which no prop fades in the cone either, and the gate by density.
 */
export interface SightConeRule {
  readonly angle: number;
  readonly reach: number;
  readonly alpha: number;
  readonly turnMs: number;
  readonly fadeMs: number;
  readonly minHeight: number;
  readonly dense: SightDenseRule;
}

/**
 * The rule the game fades props ahead of its local character by.
 */
export const SIGHT_CONE: SightConeRule = {
  angle: SIGHT_CONE_ANGLE,
  reach: SIGHT_CONE_REACH,
  alpha: SIGHT_CONE_ALPHA,
  turnMs: SIGHT_CONE_TURN_MS,
  fadeMs: SIGHT_CONE_FADE_MS,
  minHeight: OCCLUDER_MIN_HEIGHT,
  dense: { radius: SIGHT_DENSE_RADIUS, onAt: SIGHT_DENSE_ON, offBelow: SIGHT_DENSE_OFF, easeMs: SIGHT_DENSE_EASE_MS, refreshMs: SIGHT_DENSE_REFRESH_MS },
};

/**
 * The palette entry the local character's silhouette is filled with while a building part hides it (the author's
 * ruling of 2026-10-03, ADR-040's amendment of that day): vellum, the text colour, which reads on wood, stone and
 * shadow alike. Tunable.
 */
export const SILHOUETTE_COLOUR: PaletteName = "vellum-100";

/**
 * The silhouette's alpha: enough to find the character through a wall, little enough that the wall still reads as
 * solid. Tunable.
 */
export const SILHOUETTE_ALPHA = 0.5;

/**
 * The share of the local character's body box building parts must hide, together, for the silhouette to show: the
 * share at which the occluder fade fades any other prop (`OCCLUDER_MIN_COVERAGE`), counted over the union of the parts,
 * since a wall, a door and a porch often hide a body between them. The silhouette is not masked to the part, so the uncovered slice of the body draws filled as well;
 * a high share keeps that slice small. Tunable.
 */
export const SILHOUETTE_COVERAGE = OCCLUDER_MIN_COVERAGE;

/**
 * The silhouette's knobs together: the palette entry, the alpha and the share of the body that shows it.
 */
export interface SilhouetteRule {
  readonly colour: PaletteName;
  readonly alpha: number;
  readonly coverage: number;
}

/**
 * The rule the game draws its local character's silhouette by.
 */
export const SILHOUETTE: SilhouetteRule = { colour: SILHOUETTE_COLOUR, alpha: SILHOUETTE_ALPHA, coverage: SILHOUETTE_COVERAGE };
